/**
 * Design Memory — semantic fingerprint.
 *
 * Reduces a design's creative attributes (a DesignDescriptor) to a normalized,
 * comparable, stable-hashed fingerprint. Purely semantic — never pixels,
 * images, or screenshots. Deterministic: the same descriptor always yields the
 * same fingerprint (including its id).
 *
 * A future embedding is additive: a fingerprint may later carry an `embedding`
 * field without changing this contract or any consumer.
 */

import { FINGERPRINT_SCHEMA_ID, FINGERPRINT_VERSION } from './memoryVocabulary';

const norm = (v) => (typeof v === 'string' && v.trim() ? v.trim().toLowerCase() : null);

/** Ordered, lowercased, non-empty list (order preserved — matters for sequences). */
const normList = (v) => (Array.isArray(v) ? v.map(norm).filter(Boolean) : []);

/** Sorted, deduped, lowercased set (order irrelevant). */
const normSet = (v) => [...new Set(normList(v))].sort();

/** Tokenize a concept phrase into a sorted set of words. */
const tokenizeConcept = (v) =>
  typeof v === 'string'
    ? [...new Set(v.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 1))].sort()
    : [];

/** djb2 → base36; deterministic content hash for the fingerprint id. */
const hash = (str) => {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  return h.toString(36);
};

/**
 * Build a fingerprint from a design descriptor.
 * @param {Object} descriptor
 * @param {Object} [opts]
 * @param {string} [opts.id]  Override the derived id (else a stable content hash).
 * @returns {Object} a frozen fingerprint.
 */
export const createFingerprint = (descriptor, { id } = {}) => {
  const d = descriptor && typeof descriptor === 'object' && !Array.isArray(descriptor) ? descriptor : {};

  const content = {
    concept: norm(d.concept),
    conceptTokens: tokenizeConcept(d.concept),
    composition: norm(d.composition),
    hierarchy: normList(d.hierarchy),
    grid: norm(d.grid),
    sectionOrder: normList(d.sectionOrder),
    heroPlacement: norm(d.heroPlacement),
    typography: norm(d.typography),
    color: norm(d.color),
    shape: norm(d.shape),
    decoration: norm(d.decoration),
    photography: norm(d.photography),
    illustration: norm(d.illustration),
    cta: norm(d.cta),
    spacing: norm(d.spacing),
    rhythm: norm(d.rhythm),
    premiumCues: normSet(d.premiumCues),
    genome: norm(d.genome),
    libraryPatterns: normSet(d.libraryPatterns),
    componentUsage: normSet(d.componentUsage),
    visualDensity: norm(d.visualDensity),
    balance: norm(d.balance),
    negativeSpace: norm(d.negativeSpace),
    objectRelationships: normSet(d.objectRelationships),
  };

  return Object.freeze({
    schema: FINGERPRINT_SCHEMA_ID,
    version: FINGERPRINT_VERSION,
    id: id || `fp_${hash(JSON.stringify(content))}`,
    ...content,
  });
};
